import { useState } from 'preact/hooks';
import { ProductMixAndMatch as ProductMixAndMatchVM } from '../../../models/ProductMixAndMatch';
import ProductPackSelectorItem from '../../product/pack-selector/ProductPackSelectorItem';
import './ProductMixAndMatchItem.less';
import type { Product } from '../../../models/Product';
import Icon from '../../icon/Icon';
import Button from '../../button/Button';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatCurrency } from '../../../services/FormatterService';

export type ProductMixAndMatchItemProps = {
    fixedProducts: Product[];
    group: ProductMixAndMatchVM;
    onAddToCart: (products: Product[]) => void;
    source: 'brilliant' | 'web';
};

const ProductMixAndMatchItem = ({
    fixedProducts: [fixedProduct],
    group,
    onAddToCart,
    source
}: ProductMixAndMatchItemProps) => {
    const { t } = useTranslation();
    const [visibleProductIndexes, setVisibleProductIndexes] = useState<
        number[]
    >(Array.from(new Array(group.categorizedGroups.length), () => 0));
    const [selectedSlots, setSelectedSlots] = useState<number[]>(
        group.categorizedGroups.map((_, index) => index)
    );
    const selectedProducts = [
        fixedProduct,
        ...selectedSlots.map(
            slot =>
                group.categorizedGroups[slot].products[
                    visibleProductIndexes[slot]
                ]
        )
    ];
    const totalPrice = selectedProducts.reduce(
        (total, product) => total + product.unitPrice,
        0
    );
    const emptyCells = 3 - group.categorizedGroups.length;
    return (
        <div className={`bri-ProductMixAndMatchItem`}>
            <div className="bri-ProductMixAndMatchItem-title bri-TitleBoldFont">
                {group.title}
            </div>
            <div className="bri-ProductMixAndMatchItem-products">
                <ProductPackSelectorItem
                    product={fixedProduct}
                    footerContent={t('PRODUCT_MIX_AND_MATCH_CURRENT_PRODUCT')}
                    source={source}
                />

                <div className="bri-ProductMixAndMatchItem-plus">
                    <Icon iconId="plus" size="medium" />
                </div>
                {group.categorizedGroups.map((categorizedGroup, index) => (
                    <ProductPackSelectorItem
                        animationDelay={index * 100}
                        key={
                            (categorizedGroup.categoryFilter?.categoryValue ??
                                '') + index
                        }
                        product={
                            categorizedGroup.products[
                                visibleProductIndexes[index]
                            ]
                        }
                        footerContent={
                            categorizedGroup.categoryFilter
                                ?.categoryTranslatedValue
                        }
                        hasMore={categorizedGroup.products.length > 1}
                        showPagination={categorizedGroup.products.length > 1}
                        isSelectable
                        isSelected={selectedSlots.includes(index)}
                        source={source}
                        onChangeSelection={() => {
                            setSelectedSlots(
                                selectedSlots.includes(index)
                                    ? selectedSlots.filter(
                                          slot => slot !== index
                                      )
                                    : [...selectedSlots, index]
                            );
                        }}
                        onNext={() => {
                            const currentIndex = visibleProductIndexes[index];
                            const nextIndex =
                                (currentIndex + 1) %
                                categorizedGroup.products.length;
                            const newProducts = [...visibleProductIndexes];
                            newProducts[index] = nextIndex;
                            setVisibleProductIndexes(newProducts);
                        }}
                        onPrev={() => {
                            const currentIndex = visibleProductIndexes[index];
                            const nextIndex =
                                (currentIndex -
                                    1 +
                                    categorizedGroup.products.length) %
                                categorizedGroup.products.length;
                            const newProducts = [...visibleProductIndexes];
                            newProducts[index] = nextIndex;
                            setVisibleProductIndexes(newProducts);
                        }}
                    />
                ))}
                {Array.from(new Array(emptyCells)).map((_, index) => (
                    <div
                        key={index}
                        className="bri-ProductMixAndMatchItem-empty-cell"
                    ></div>
                ))}
            </div>

            <div className="bri-ProductMixAndMatchItem-bottom-card">
                <div className="bri-ProductMixAndMatchItem-summary">
                    <div className="bri-ProductMixAndMatchItem-description">
                        <div className="bri-DisplayFont">
                            {group.description}{' '}
                            <span className="bri-DisplayBoldFont">
                                {group.tips[0]}
                            </span>{' '}
                            ✨
                        </div>
                    </div>
                    <div className="bri-ProductMixAndMatchItem-summary-separator"></div>
                    <div className="bri-ProductMixAndMatchItem-summary-total-container">
                        <div
                            className={
                                'bri-ProductMixAndMatchItem-summary-total-container-texts'
                            }
                        >
                            <div className="bri-BodyFont">{t('TOTAL')}</div>
                            <div className="bri-ProductMixAndMatchItem-summary-total-price bri-TitleBoldFont">
                                {formatCurrency(
                                    totalPrice,
                                    selectedProducts[0].currency,
                                    selectedProducts[0].locale
                                )}
                            </div>
                        </div>
                        <Button
                            variant="magic"
                            size="large"
                            onClick={() => onAddToCart(selectedProducts)}
                        >
                            {t('ADD_TO_CART_BUTTON_LABEL_MANY', {
                                count: selectedProducts.length
                            })}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductMixAndMatchItem;
